import { NmLoader } from '../components/NmLoader';
import { displayDigits } from '../utils/displayDigits';
import {WorkoutTypeIcon} from '../components/WorkoutTypeIcon';
import { canResumeRegistrationConnections } from '../utils/pendingRegistrationConnections';
import {normalizeInputDigits} from '../utils/localizedInput';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../components/NmButton';
import { NmCard } from '../components/NmCard';
import { NmInput } from '../components/NmInput';
import { NmWizard, type WizardStepDef } from '../components/NmWizard';
import { ConnectionsPanel } from '../components/athlete-settings/ConnectionsPanel';
import type { CompleteRegisterPayload, RegisterPayload } from '../services/api';

type SportType = 'runner' | 'cyclist' | 'swimmer' | '';
type RegisterStep = 'mobile' | 'password' | 'personal' | 'role' | 'sport' | 'connections';

const SPORT_TYPE_ID: Record<string, number> = {
  cyclist: 1,
  swimmer: 2,
  runner: 3,
};

const SPORTS: {
  key: SportType;
  labelKey: string;
  descKey: string;
  color: string;
  gradient: string;
  icon: React.ReactNode;
}[] = [
  {
    key: 'runner',
    labelKey: 'register.sport.runner',
    descKey: 'register.sport.runnerDesc',
    color: '#1FAE63',
    gradient: 'linear-gradient(135deg, #1FAE63 0%, #0d7a42 100%)',
    icon: <RunnerIcon />,
  },
  {
    key: 'cyclist',
    labelKey: 'register.sport.cyclist',
    descKey: 'register.sport.cyclistDesc',
    color: 'var(--text-accent)',
    gradient: 'linear-gradient(135deg, var(--palette-brand) 0%, var(--palette-brand-ink) 100%)',
    icon: <CyclistIcon />,
  },
  {
    key: 'swimmer',
    labelKey: 'register.sport.swimmer',
    descKey: 'register.sport.swimmerDesc',
    color: '#29D4C6',
    gradient: 'linear-gradient(135deg, #29D4C6 0%, #1a9e94 100%)',
    icon: <SwimmerIcon />,
  },
];

export interface RegisterPageProps {
  /** Development-only visual review; never sends registration requests. */
  designReview?: boolean;
  initialMobile?: string;
  initialToken?: string;
  userId?: number;
  onRegister: (payload: RegisterPayload) => Promise<void>;
  onCompleteRegister: (payload: CompleteRegisterPayload, token: string) => Promise<void>;
  onCheckMobile: (mobile: string) => Promise<{ exists: boolean; completed: boolean }>;
  onSwitchToSignIn?: (mobile?: string) => void;
  onAccountCreated?: () => void;
  onAccountCreationFailed?: () => void;
  onFinish?: () => void;
}

/** Multi-step registration wizard — mirrors mobile flow with bridge neumorphic design. */
export function RegisterPage({
  designReview = false,
  initialMobile = '',
  initialToken = '',
  userId,
  onRegister,
  onCompleteRegister,
  onCheckMobile,
  onSwitchToSignIn,
  onAccountCreated,
  onAccountCreationFailed,
  onFinish,
}: RegisterPageProps) {
  const { t, i18n } = useTranslation();
  const needsMobile = !initialToken && !initialMobile;
  const needsPassword = !initialToken;

  const [step, setStep] = useState<RegisterStep>(() => {
    if (canResumeRegistrationConnections(userId)) return 'connections';
    if (initialToken) return 'personal';
    if (initialMobile) return 'password';
    return 'mobile';
  });

  const [mobile, setMobile] = useState(initialMobile);
  const [password, setPassword] = useState('');
  const [authToken] = useState(initialToken);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [role, setRole] = useState('');

  const stepFlow = useMemo<RegisterStep[]>(() => {
    const flow: RegisterStep[] = [];
    if (needsMobile) flow.push('mobile');
    if (needsPassword) flow.push('password');
    flow.push('personal', 'role');
    if (role !== '3') flow.push('sport');
    flow.push('connections');
    return flow;
  }, [needsMobile, needsPassword, role]);

  const [sport, setSport] = useState<SportType>('');
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [lthr, setLthr] = useState('');
  const [ftp, setFtp] = useState('');

  const [error, setError] = useState('');
  const [connectionMsg, setConnectionMsg] = useState('');
  const [accountCreated, setAccountCreated] = useState(() => canResumeRegistrationConnections(userId));
  const [mobileAlreadyRegistered, setMobileAlreadyRegistered] = useState(false);
  const [loading, setLoading] = useState(false);

  const clearError = () => setError('');

  /** Keep wizard step count stable on role selection — sport appears only after leaving role. */
  const showSportWizardStep = step === 'sport' || (role === '2' && step !== 'role');

  const showConnectionsWizardStep =
    step === 'connections' ||
    accountCreated ||
    (role === '3' && step !== 'role') ||
    (role === '2' && showSportWizardStep);

  const wizardSteps = useMemo<WizardStepDef[]>(() => {
    const statusFor = (target: RegisterStep): WizardStepDef['status'] => {
      const order: RegisterStep[] = ['mobile', 'password', 'personal', 'role', 'sport', 'connections'];
      const currentIdx = order.indexOf(step);
      const targetIdx = order.indexOf(target);
      if (currentIdx === targetIdx) return 'current';
      if (currentIdx > targetIdx) return 'done';
      return 'upcoming';
    };

    const steps: WizardStepDef[] = [];

    if (needsMobile) {
      steps.push({ title: t('register.step.mobile'), status: statusFor('mobile') });
    }
    if (needsPassword) {
      steps.push({ title: t('register.step.password'), status: statusFor('password') });
    }
    steps.push({ title: t('register.step.profile'), status: statusFor('personal') });
    steps.push({ title: t('register.step.role'), status: statusFor('role') });
    if (showSportWizardStep) {
      steps.push({ title: t('register.step.sport'), status: statusFor('sport') });
    }
    if (showConnectionsWizardStep) {
      steps.push({ title: t('register.step.connections'), status: statusFor('connections') });
    }

    return steps;
  }, [needsMobile, needsPassword, showSportWizardStep, showConnectionsWizardStep, step, t]);

  const handleCheckMobile = useCallback(async () => {
    const normalized = mobile.trim();
    if (!/^09\d{9}$/.test(normalized)) {
      setError(t('register.error.invalidMobile'));
      return;
    }

    clearError();
    setLoading(true);
    try {
      const result = await onCheckMobile(normalized);
      if (result.exists) {
        setMobileAlreadyRegistered(true);
        setError(t('register.error.alreadyRegistered'));
        return;
      }
      setMobileAlreadyRegistered(false);
      setStep('password');
    } catch {
      setError(t('register.error.serverError'));
    } finally {
      setLoading(false);
    }
  }, [mobile, onCheckMobile, t]);

  const handlePasswordNext = () => {
    if (password.trim().length < 6) {
      setError(t('register.error.passwordShort'));
      return;
    }
    clearError();
    setStep('personal');
  };

  const submit = useCallback(async (): Promise<boolean> => {
    clearError();
    setLoading(true);
    try {
      const base: CompleteRegisterPayload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender,
        role_id: role,
      };

      if (role === '2') {
        base.sport_type_id = String(SPORT_TYPE_ID[sport] ?? '');
        base.weight = weight;
        base.age = age;
        base.lthr = lthr;
        if (sport === 'cyclist') {
          base.ftp = ftp;
        }
      }

      if (authToken) {
        await onCompleteRegister(base, authToken);
      } else {
        await onRegister({
          mobile: mobile.trim(),
          password: password.trim(),
          ...base,
        });
      }
      return true;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        t('register.error.registrationFailed');
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    age,
    authToken,
    firstName,
    ftp,
    gender,
    lastName,
    lthr,
    mobile,
    onCompleteRegister,
    onRegister,
    password,
    role,
    sport,
    weight,
    t,
  ]);

  const submitAndGoConnections = useCallback(async () => {
    onAccountCreated?.();
    const ok = await submit();
    if (!ok) {
      onAccountCreationFailed?.();
      return;
    }
    setAccountCreated(true);
    setStep('connections');
  }, [onAccountCreated, onAccountCreationFailed, submit]);

  const goNext = () => {
    if (step === 'mobile') {
      void handleCheckMobile();
      return;
    }
    if (step === 'password') {
      handlePasswordNext();
      return;
    }
    if (step === 'role' && role === '3') {
      void submitAndGoConnections();
      return;
    }

    const idx = stepFlow.indexOf(step);
    if (idx >= 0 && idx < stepFlow.length - 1) {
      setStep(stepFlow[idx + 1]);
    }
  };

  const goBack = () => {
    if (step === 'connections') return;
    const idx = stepFlow.indexOf(step);
    if (idx > 0) {
      setStep(stepFlow[idx - 1]);
    }
    clearError();
  };

  const isMobileValid = /^09\d{9}$/.test(mobile.trim());
  const isPasswordValid = password.trim().length >= 6;
  const isPersonalValid = !!(firstName.trim() && lastName.trim() && gender);
  const isRoleValid = !!role;
  const isSportValid = !!(sport && weight && age && lthr && (sport !== 'cyclist' || ftp));

  const canGoNext =
    (step === 'mobile' && isMobileValid) ||
    (step === 'password' && isPasswordValid) ||
    (step === 'personal' && isPersonalValid) ||
    (step === 'role' && isRoleValid);

  const showBack = step !== 'connections' && stepFlow.indexOf(step) > 0;

  return (
    <div className="register-wizard animate-fadein" data-step={step}>
      <NmWizard steps={wizardSteps} />

      <div className="register-wizard__body">
      {error && step !== 'mobile' && step !== 'password' && (
        <div style={styles.errorBanner}>{error}</div>
      )}

      {/* Step: Mobile */}
      {step === 'mobile' && (
        <div style={styles.form} className="register-wizard__step">
          <h3 style={styles.stepTitle}>{t('register.createTitle')}</h3>
          <p style={styles.stepSubtitle}>{t('register.createSubtitle')}</p>
          <NmInput
            label={t('register.field.mobile')}
            type="tel"
            placeholder={displayDigits(t('register.mobilePlaceholder'), i18n.language)}
            value={displayDigits(mobile, i18n.language)}
            onChange={(e) => {
              setMobile(normalizeInputDigits(e.target.value));
              clearError();
            }}
            onKeyDown={(e) => e.key === 'Enter' && canGoNext && goNext()}
            autoFocus
            icon={<PhoneIcon />}
            error={error}
          />
          <NmButton
            variant="primary"
            size="sm"
            className="register-action-btn register-action-btn--full"
            loading={loading}
            disabled={!canGoNext}
            onClick={goNext}
          >
            {t('register.action.continue')}
          </NmButton>
          {onSwitchToSignIn && (
            <button type="button" onClick={() => onSwitchToSignIn(mobile)} style={styles.linkBtn}>
              {mobileAlreadyRegistered
                ? t('register.action.signInRegistered')
                : t('register.action.signInExisting')}
            </button>
          )}
        </div>
      )}

      {/* Step: Password */}
      {step === 'password' && (
        <div style={styles.form} className="register-wizard__step">
          <h3 style={styles.stepTitle}>{t('register.passwordTitle')}</h3>
          <p style={styles.stepSubtitle}>{t('register.passwordSubtitle', { mobile: displayDigits(mobile, i18n.language) })}</p>
          <NmInput
            label={t('register.field.password')}
            type="password"
            placeholder={t('register.field.passwordPlaceholder')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearError();
            }}
            onKeyDown={(e) => e.key === 'Enter' && canGoNext && goNext()}
            autoFocus
            error={error}
            hint={t('register.field.passwordHint')}
          />
          <NmButton
            variant="primary"
            size="sm"
            className="register-action-btn register-action-btn--full"
            disabled={!canGoNext}
            onClick={goNext}
          >
            {t('register.action.continue')}
          </NmButton>
          {showBack && (
            <NmButton variant="ghost" size="sm" className="register-nav-btn register-back-btn--solo" onClick={goBack}>
              {t('register.action.back')}
            </NmButton>
          )}
        </div>
      )}

      {/* Step: Personal */}
      {step === 'personal' && (
        <div style={styles.form} className="register-wizard__step">
          <h3 style={styles.stepTitle}>{t('register.personalTitle')}</h3>
          <p style={styles.stepSubtitle}>{t('register.personalSubtitle')}</p>
          <div className="register-personal-grid" style={styles.twoCol}>
            <NmInput
              label={t('register.field.firstName')}
              placeholder={t('register.firstNamePlaceholder')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <NmInput
              label={t('register.field.lastName')}
              placeholder={t('register.field.lastNamePlaceholder')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <span className="nm-input-label">{t('register.field.gender')}</span>
          <div className="register-gender-row" style={styles.genderRow}>
            {([
              { labelKey: 'register.field.female', value: '2', icon: <FemaleIcon /> },
              { labelKey: 'register.field.male', value: '1', icon: <MaleIcon /> },
            ] as const).map((opt) => {
              const active = gender === opt.value;
              return (
                <NmCard
                  key={opt.value}
                  className={`register-gender-card${active ? ' is-selected' : ''}`}
                  variant={active ? 'raised-lg' : 'raised'}
                  onClick={() => setGender(opt.value)}
                  style={{
                    ...styles.genderCard,
                    border: active ? '2px solid var(--vo2-pink-500)' : '2px solid transparent',
                  }}
                >
                  <span style={{ color: active ? 'var(--vo2-pink-500)' : 'var(--fg-3)' }}>
                    {opt.icon}
                  </span>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--fs-small)',
                      color: active ? 'var(--vo2-pink-500)' : 'var(--fg-2)',
                    }}
                  >
                    {t(opt.labelKey)}
                  </span>
                </NmCard>
              );
            })}
          </div>
          <div className="register-nav-row">
            {showBack && (
              <NmButton variant="ghost" size="sm" className="register-nav-btn" onClick={goBack}>
                {t('register.action.back')}
              </NmButton>
            )}
            <NmButton
              variant="primary"
              size="sm"
              className="register-nav-btn register-nav-btn--primary"
              disabled={!canGoNext}
              onClick={goNext}
            >
              {t('register.action.continue')}
            </NmButton>
          </div>
        </div>
      )}

      {/* Step: Role */}
      {step === 'role' && (
        <div style={styles.form} className="register-wizard__step">
          <h3 style={styles.stepTitle}>{t('register.roleTitle')}</h3>
          <p style={styles.stepSubtitle}>{t('register.roleSubtitle')}</p>
          <div className="register-role-row">
            <RoleCard
              title={t('register.role.athlete')}
              desc={t('register.role.athleteDesc')}
              active={role === '2'}
              accent="var(--vo2-pink-500)"
              icon={<AthleteIcon />}
              onClick={() => {
                setRole('2');
                clearError();
              }}
            />
            <RoleCard
              title={t('register.role.coach')}
              desc={t('register.role.coachDesc')}
              active={role === '3'}
              accent="var(--vo2-green-500)"
              icon={<CoachIcon />}
              onClick={() => {
                setRole('3');
                clearError();
              }}
            />
          </div>
          {error && <div style={styles.errorBanner}>{error}</div>}
          <div className="register-nav-row">
            <NmButton variant="ghost" size="sm" className="register-nav-btn" onClick={goBack}>
              {t('register.action.back')}
            </NmButton>
            <NmButton
              variant="primary"
              size="sm"
              className="register-nav-btn register-nav-btn--primary"
              loading={loading && role === '3'}
              disabled={!isRoleValid}
              onClick={goNext}
            >
              {role === '3' ? t('register.action.createContinue') : t('register.action.continue')}
            </NmButton>
          </div>
        </div>
      )}

      {/* Step: Sport profile */}
      {step === 'sport' && (
        <div style={styles.form} className="register-wizard__step">
          <h3 style={styles.stepTitle}>{t('register.sportTitle')}</h3>
          <p style={styles.stepSubtitle}>{t('register.sportSubtitle')}</p>

          <span className="nm-input-label">{t('register.sportLabel')}</span>
          <div className="register-sport-row" style={styles.sportRow}>
            {SPORTS.map((s) => {
              const active = sport === s.key;
              return (
                <button
                  key={s.key}
                  className="register-sport-choice"
                  aria-pressed={active}
                  type="button"
                  onClick={() => setSport(s.key)}
                  style={{
                    ...styles.sportCard,
                    background: s.gradient,
                    boxShadow: active ? `0 0 0 3px ${s.color}, var(--nm-raised-lg)` : 'var(--nm-raised)',
                  }}
                >
                  <span style={styles.sportIconWrap}>{s.icon}</span>
                  <span style={styles.sportLabel}>{t(s.labelKey)}</span>
                  <span style={styles.sportDesc}>{t(s.descKey)}</span>
                  {active && (
                    <span style={{ ...styles.sportCheck, background: s.color }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {sport !== '' && (
            <div className="register-sport-details animate-fadein">
              <span className="nm-input-label register-sport-details__heading">{t('register.bodyStats')}</span>
              <div className="register-stats-grid">
                <NmInput
                  className="register-sport-input"
                  label={t('register.field.age')}
                  type="text"
                  inputMode="numeric"
                  placeholder={displayDigits('25', i18n.language)}
                  value={displayDigits(age, i18n.language)}
                  onChange={(e) => { const next = normalizeInputDigits(e.target.value).replace('٫', '.'); if (/^\d*(?:\.\d*)?$/.test(next)) setAge(next); }}
                  hint={t('register.hint.years')}
                />
                <NmInput
                  className="register-sport-input"
                  label={t('register.field.weight')}
                  type="text"
                  inputMode="numeric"
                  placeholder={displayDigits('70', i18n.language)}
                  value={displayDigits(weight, i18n.language)}
                  onChange={(e) => { const next = normalizeInputDigits(e.target.value).replace('٫', '.'); if (/^\d*(?:\.\d*)?$/.test(next)) setWeight(next); }}
                  hint={t('register.hint.kg')}
                />
              </div>

              <div className="register-metric-card">
                <div className="register-metric-card__header">
                  <span className="register-metric-card__badge" data-metric="lthr">
                    LTHR
                  </span>
                  <div className="register-metric-card__copy">
                    <p className="register-metric-card__title">{t('register.lthrBlock.title')}</p>
                    <p className="register-metric-card__hint">{t('register.lthrBlock.hint')}</p>
                  </div>
                </div>
                <NmInput
                  className="register-sport-input"
                  type="text"
                  inputMode="numeric"
                  aria-label={t('register.lthrBlock.title')}
                  placeholder={displayDigits('165', i18n.language)}
                  value={displayDigits(lthr, i18n.language)}
                  onChange={(e) => { const next = normalizeInputDigits(e.target.value).replace('٫', '.'); if (/^\d*(?:\.\d*)?$/.test(next)) setLthr(next); }}
                  hint={t('register.hint.bpm')}
                />
              </div>

              {sport === 'cyclist' && (
                <div className="register-metric-card">
                  <div className="register-metric-card__header">
                    <span className="register-metric-card__badge" data-metric="ftp">
                      FTP
                    </span>
                    <div className="register-metric-card__copy">
                      <p className="register-metric-card__title">{t('register.ftpBlock.title')}</p>
                      <p className="register-metric-card__hint">{t('register.ftpBlock.hint')}</p>
                    </div>
                  </div>
                  <NmInput
                    className="register-sport-input"
                    type="text"
                    inputMode="numeric"
                    aria-label={t('register.ftpBlock.title')}
                    placeholder={displayDigits('250', i18n.language)}
                    value={displayDigits(ftp, i18n.language)}
                    onChange={(e) => { const next = normalizeInputDigits(e.target.value).replace('٫', '.'); if (/^\d*(?:\.\d*)?$/.test(next)) setFtp(next); }}
                    hint={t('register.hint.watts')}
                  />
                </div>
              )}
            </div>
          )}

          {error && <div style={styles.errorBanner}>{error}</div>}
          <div className="register-nav-row">
            <NmButton variant="ghost" size="sm" className="register-nav-btn" onClick={goBack}>
              {t('register.action.back')}
            </NmButton>
            <NmButton
              variant="primary"
              size="sm"
              className="register-nav-btn register-nav-btn--primary"
              loading={loading}
              disabled={!isSportValid}
              onClick={() => void submitAndGoConnections()}
            >
              {t('register.action.createContinue')}
            </NmButton>
          </div>
        </div>
      )}

      {/* Step: Connections */}
      {step === 'connections' && (
        <div style={styles.form} className="register-wizard__step">
          <h3 style={styles.stepTitle}>{t('register.connectionsTitle')}</h3>
          <p style={styles.stepSubtitle}>{t('register.connectionsSubtitle')}</p>
          {connectionMsg && (
            <div className="register-connection-toast">{connectionMsg}</div>
          )}
          {(import.meta.env.DEV && designReview) || userId != null ? (
            <ConnectionsPanel
              athleteId={userId ?? 0}
              preview={import.meta.env.DEV && designReview}
              connectedApps={import.meta.env.DEV && designReview ? {} : undefined}
              variant="onboarding"
              onMessage={setConnectionMsg}
            />
          ) : (
            <p className="connections-panel__loading"><NmLoader size="sm" inline label={t('common.loading')} /></p>
          )}
          <div className="register-nav-row register-nav-row--finish">
            <NmButton
              variant="ghost"
              size="sm"
              className="register-nav-btn"
              onClick={() => onFinish?.()}
            >
              {t('register.action.skip')}
            </NmButton>
            <NmButton
              variant="primary"
              size="sm"
              className="register-nav-btn register-nav-btn--primary"
              onClick={() => onFinish?.()}
            >
              {t('register.action.finish')}
            </NmButton>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function RoleCard({
  title,
  desc,
  active,
  accent,
  icon,
  onClick,
}: {
  title: string;
  desc: string;
  active: boolean;
  accent: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <NmCard
      variant={active ? 'raised-lg' : 'raised'}
      onClick={onClick}
      className={`register-role-card${active ? ' is-selected' : ''}`}
      style={{
        border: active ? `2px solid ${accent}` : '2px solid transparent',
      }}
    >
      {active && (
        <span className="register-role-card__dot" style={{ background: accent }} />
      )}
      <span style={{ color: active ? accent : 'var(--fg-3)' }}>{icon}</span>
      <span className="register-role-card__title" style={{ color: active ? accent : 'var(--fg-1)' }}>
        {title}
      </span>
      <span className="register-role-card__desc">{desc}</span>
    </NmCard>
  );
}

const styles: Record<string, React.CSSProperties> = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-4)',
    width: '100%',
    minWidth: 0,
  },
  stepTitle: {
    fontSize: 'var(--fs-h4)',
    margin: 0,
    textAlign: 'start',
    width: '100%',
  },
  stepSubtitle: {
    fontSize: 'var(--fs-small)',
    color: 'var(--fg-3)',
    margin: '0 0 4px',
    textAlign: 'start',
    width: '100%',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--sp-3)',
  },
  genderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--sp-3)',
  },
  genderCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: 'var(--sp-4)',
    cursor: 'pointer',
    transition: 'box-shadow var(--dur-1) var(--ease-out)',
  },
  sportRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'var(--sp-3)',
  },
  sportCard: {
    position: 'relative',
    border: 'none',
    borderRadius: 'var(--r-card)',
    padding: 'var(--sp-4) var(--sp-2)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    color: '#fff',
    transition: 'box-shadow var(--dur-1) var(--ease-out), transform var(--dur-1) var(--ease-out)',
  },
  sportIconWrap: {
    opacity: 0.95,
    marginBottom: 4,
  },
  sportLabel: {
    fontWeight: 700,
    fontSize: 'var(--fs-small)',
  },
  sportDesc: {
    fontSize: 'var(--fs-caption)',
    opacity: 0.85,
  },
  sportCheck: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-2)',
  },
  metricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--sp-2)',
  },
  metricBadge: {
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 'var(--fs-caption)',
    fontWeight: 700,
    color: '#fff',
    fontFamily: 'var(--font-mono)',
  },
  metricTitle: {
    fontSize: 'var(--fs-small)',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  metricHint: {
    fontSize: 'var(--fs-caption)',
    color: 'var(--fg-3)',
    margin: 0,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--vo2-pink-500)',
    fontSize: 'var(--fs-small)',
    padding: '4px 0',
    textAlign: 'center',
  },
  errorBanner: {
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.25)',
    borderRadius: 'var(--r-3)',
    padding: '10px 14px',
    fontSize: 'var(--fs-small)',
    color: '#dc2626',
  },
};

function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6.22 6.22l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function FemaleIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="8" r="5" />
      <path d="M12 13v8M9 18h6" />
    </svg>
  );
}

function MaleIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="10" cy="14" r="5" />
      <path d="M19 5l-5.4 5.4M19 5h-5M19 5v5" />
    </svg>
  );
}

function AthleteIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4M8 21l4-10 4 10M6 11h12" />
    </svg>
  );
}

function CoachIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function RunnerIcon() { return <WorkoutTypeIcon typeId={1} size={32} />; }

function CyclistIcon() { return <WorkoutTypeIcon typeId={2} size={32} />; }

function SwimmerIcon() { return <WorkoutTypeIcon typeId={3} size={32} />; }
